import { useState } from "react";
import { MapPin, ExternalLink } from "lucide-react";

/**
 * MapEmbed — Facade for Google Maps iframe.
 *
 * WHY: Loading an iframe (especially google.com/maps) during initial page
 * render blocks headless browsers / crawler-caches waiting for `networkidle`.
 * We render a static clickable card by default and only mount the real
 * <iframe> once the user explicitly asks for it.
 *
 * Benefits:
 *  - Crawler-cache captures the page instantly (no external iframe to wait for)
 *  - Real users still get the interactive map on click
 *  - A permanent "Ouvrir dans Google Maps" fallback anchor exists for those
 *    who prefer the full app, and for crawlers to follow.
 */
export default function MapEmbed({
    address = "Chemin du Pré-Fleuri 3, 1228 Plan-les-Ouates, Suisse",
    label = "Localisation RCPS Terrassement",
    testId = "map-embed",
}) {
    const [active, setActive] = useState(false);
    const encoded = encodeURIComponent(address);
    const embedSrc = `https://www.google.com/maps?q=${encoded}&output=embed`;
    const openSrc = `https://www.google.com/maps/search/?api=1&query=${encoded}`;

    return (
        <div
            className="relative w-full h-full bg-concrete-100"
            data-testid={testId}
        >
            {active ? (
                <iframe
                    title={label}
                    src={embedSrc}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid={`${testId}-iframe`}
                />
            ) : (
                <button
                    type="button"
                    onClick={() => setActive(true)}
                    className="group absolute inset-0 flex flex-col items-center justify-center gap-4 bg-topo-dark bg-concrete-100 hover:bg-concrete-50 transition text-navy-700"
                    aria-label="Afficher la carte Google Maps"
                    data-testid={`${testId}-activate`}
                >
                    <span className="w-14 h-14 rounded-full bg-navy-700 text-white flex items-center justify-center shadow-md group-hover:scale-105 transition">
                        <MapPin className="w-6 h-6" />
                    </span>
                    <span className="font-heading text-lg md:text-xl font-semibold text-navy-700 tracking-tight text-center px-6">
                        {address}
                    </span>
                    <span className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-navy-700 text-white font-semibold">
                        Afficher la carte
                    </span>
                    <a
                        href={openSrc}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1.5 text-sm text-navy-700/75 hover:text-navy-700 underline underline-offset-2"
                        data-testid={`${testId}-open-in-maps`}
                    >
                        Ouvrir dans Google Maps
                        <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                </button>
            )}
        </div>
    );
}
